import { useSearchParams } from 'react-router'
import { Button } from '@/components/base/buttons/button'
import { Select } from '@/components/base/select/select'
import { useTeam, useWorkList } from '@/shell/api/queries'
import { Body, Empty, Skeleton } from '@/shell/components/page'
import { PageBar } from '@/shell/components/page-bar'
import { STATUS_LABEL } from '@/shell/components/status-pill'
import { periodLabel } from '@/shell/period'
import { useShell } from '@/shell/store'
import { cx } from '@/utils/cx'
import { NoAccessPage } from '../auth/no-access'
import { ItemList } from './my-work'

/**
 * PRD-018 v0.4 §3.18 / PRD-017 §3.6 v0.19 (Seb UX review note 28, decision 1): Work, the firm-wide work-item list.
 * The My work grid at firm scope with filters client / assignee / status / kind / overdue in the URL, period-aware
 * (close rows follow the header month; tasks and recurring items with no period show regardless, tagged), a facts
 * line (open · waiting on a person · overdue · done this period) from the same request, cursor-paged with Load more.
 * No title-row primary (§3.4: creation belongs to a page with a client context or to My work's New task). Rows open
 * the drawer at `/w/<item>` as everywhere.
 *
 * Who: CFO and Controller. A Member or Tax has no Work row and reads the no-access page here (their firm view is
 * My work). "Mine" narrows to the clients the viewer leads and the files they manage; the default is firm-wide.
 * `?unfiled=1` is wired for the personal Inbox (PRD-017 §3.13) and has no control yet: nothing is unfiled before
 * that migration lands. Row-level security trims the rows, never this page.
 *
 * Prior art: Karbon's Work list (saved views by client, assignee, status), Linear's team-scope Issues with filter
 * chips in the URL, QBOA's All Tasks, Canopy's Tasks. Adopted: one list, URL-carried filters, the shared grid.
 * Rejected: Kanban (the status set is the platform's) and saved views in the first cut.
 */

export function canSeeWork(role: string): boolean {
  return role === 'cfo' || role === 'controller'
}

const STATUS_ORDER = ['not_started', 'scheduled', 'running', 'waiting_on_you', 'review', 'blocked', 'done'] as const
const CLASS_ITEMS = [
  { id: '', label: 'Every kind' },
  { id: 'class:close', label: 'Close' },
  { id: 'class:recurring', label: 'Recurring' },
  { id: 'class:task', label: 'Task' },
  { id: 'kind:checklist', label: 'Checklist rows' },
  { id: 'kind:thread_step', label: 'Job steps' },
  { id: 'kind:review', label: 'Reviews' },
  { id: 'kind:held_email', label: 'Held emails' },
  { id: 'kind:request', label: 'Client requests' },
]

export function WorkPage() {
  const { bootstrap, period } = useShell()
  const [params, setParams] = useSearchParams()
  const team = useTeam()
  const allowed = canSeeWork(bootstrap.me.role)
  const client = params.get('client') ?? ''
  const assignee = params.get('assignee') ?? ''
  const statuses = (params.get('status') ?? '').split(',').filter(Boolean)
  const kindParam = params.get('kind') ?? ''
  const overdue = params.get('overdue') === '1'
  const mine = params.get('mine') === '1'
  const unfiled = params.get('unfiled') === '1'
  const allPeriods = params.get('all') === '1'
  const [kindOrClass, kindValue] = kindParam.includes(':') ? kindParam.split(':') : ['', '']
  const list = useWorkList({
    client: client || null,
    assignee: assignee || null,
    status: statuses.length ? statuses : null,
    kind: kindOrClass === 'kind' ? kindValue : null,
    workClass: kindOrClass === 'class' ? kindValue : null,
    overdue,
    mine,
    unfiled,
    period: allPeriods ? null : period,
  }, { enabled: allowed })
  if (!allowed)
    return <NoAccessPage />
  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(params)
    if (v === null || v === '')
      next.delete(k)
    else next.set(k, v)
    setParams(next, { replace: true })
  }
  const toggleStatus = (s: string) => {
    const next = statuses.includes(s) ? statuses.filter(x => x !== s) : [...statuses, s]
    setParam('status', next.length ? next.join(',') : null)
  }
  const names = new Map<string, { cluster: string, file: string | null }>()
  for (const t of bootstrap.tree) {
    names.set(t.cluster.id, { cluster: t.cluster.name, file: null })
    for (const f of t.files) names.set(f.id, { cluster: t.cluster.name, file: f.name })
  }
  const rows = list.data?.pages.flatMap(p => p.workItems) ?? []
  const totals = list.data?.pages[0]?.totals
  const summary = list.isPending
    ? '…'
    : totals
      ? `${totals.open} open · ${totals.waiting} waiting on a person · ${totals.overdue} overdue · ${totals.doneThisPeriod} done ${allPeriods ? 'all periods' : periodLabel(period)}`
      : `${rows.length} ${rows.length === 1 ? 'item' : 'items'}`
  const people = (team.data?.seats ?? []).filter(s => !s.deactivated_at).map(s => ({ id: s.principal_id, label: s.display_name }))
  const agents = bootstrap.tree.flatMap(t => t.cluster.agentPrincipalId ? [{ id: t.cluster.agentPrincipalId, label: `${t.cluster.name} agent` }] : [])
  const clients = bootstrap.tree.map(t => ({ id: t.cluster.id, label: t.cluster.name }))
  const chip = (on: boolean) => cx('rounded-md border border-secondary px-2.5 py-1 text-xs font-medium', on ? 'bg-brand-primary text-brand-secondary' : 'text-tertiary hover:bg-primary_hover')
  return (
    <>
      <PageBar crumbs={[{ label: 'Work' }]} followsPeriod={!allPeriods} />
      <div className="px-8 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-primary" data-testid="page-title">Work</h1>
            <p className="mt-0.5 text-sm text-tertiary" data-testid="work-summary">{summary}</p>
          </div>
          {/* §3.4 title-row rule: Work has no primary; creation lives where a client context exists or on My work. */}
          <div className="flex items-center gap-2" data-testid="title-actions" />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 border-b border-secondary pb-3" data-testid="work-filters">
          <div role="group" aria-label="Status" className="flex flex-wrap gap-1 rounded-lg border border-secondary p-0.5">
            <button type="button" onClick={() => setParam('status', null)} aria-pressed={statuses.length === 0} className={cx('rounded-md px-2.5 py-1 text-xs font-medium', statuses.length === 0 ? 'bg-brand-primary text-brand-secondary' : 'text-tertiary hover:bg-primary_hover')} data-testid="work-filter-status-open">All open</button>
            {STATUS_ORDER.map(s => (
              <button key={s} type="button" onClick={() => toggleStatus(s)} aria-pressed={statuses.includes(s)} className={cx('rounded-md px-2.5 py-1 text-xs font-medium', statuses.includes(s) ? 'bg-brand-primary text-brand-secondary' : 'text-tertiary hover:bg-primary_hover')} data-testid={`work-filter-status-${s}`}>
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setParam('overdue', overdue ? null : '1')} aria-pressed={overdue} className={chip(overdue)} data-testid="work-filter-overdue">Overdue</button>
          <button type="button" onClick={() => setParam('mine', mine ? null : '1')} aria-pressed={mine} className={chip(mine)} data-testid="work-filter-mine" title="Clients you lead and files you manage">Mine</button>
          <button type="button" onClick={() => setParam('all', allPeriods ? null : '1')} aria-pressed={allPeriods} className={chip(allPeriods)} data-testid="work-filter-all-periods">
            {allPeriods ? 'All periods' : `${periodLabel(period)} only`}
          </button>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="w-52">
              <Select aria-label="Client" size="sm" placeholder="Every client" items={[{ id: '', label: 'Every client' }, ...clients]} selectedKey={client} onSelectionChange={k => setParam('client', k === null || k === '' ? null : String(k))} data-testid="work-filter-client">
                {item => <Select.Item id={item.id} label={item.label} />}
              </Select>
            </div>
            <div className="w-52">
              <Select aria-label="Assignee" size="sm" placeholder="Anyone" items={[{ id: '', label: 'Anyone' }, ...people, ...agents]} selectedKey={assignee} onSelectionChange={k => setParam('assignee', k === null || k === '' ? null : String(k))} data-testid="work-filter-assignee">
                {item => <Select.Item id={item.id} label={item.label} />}
              </Select>
            </div>
            <div className="w-44">
              <Select aria-label="Kind" size="sm" placeholder="Every kind" items={CLASS_ITEMS} selectedKey={kindParam} onSelectionChange={k => setParam('kind', k === null || k === '' ? null : String(k))} data-testid="work-filter-kind">
                {item => <Select.Item id={item.id} label={item.label} />}
              </Select>
            </div>
          </div>
        </div>
        <p className="mt-2 text-xs text-quaternary" data-testid="work-filter-note">
          {allPeriods ? 'Items across every period.' : `Close rows for ${periodLabel(period)}; tasks and recurring items with no period show regardless. Rows outside your scope are read-only.`}
        </p>
      </div>
      <Body>
        <div className="col-span-12" data-testid="work-list">
          {list.isPending
            ? <Skeleton rows={6} />
            : list.isError
              ? (
                  <div className="rounded-lg border border-secondary p-4 text-sm text-tertiary" role="alert">
                    Could not load the list. Check your connection and retry.
                    <Button size="sm" color="secondary" className="ml-3" onClick={() => list.refetch()}>Retry</Button>
                  </div>
                )
              : rows.length === 0
                ? <Empty>{overdue ? 'Nothing is overdue with these filters.' : mine ? 'Nothing open on the clients you lead or the files you manage.' : 'Nothing open with these filters. Widen a filter, or switch to All periods.'}</Empty>
                : (
                    <>
                      <ItemList items={rows} names={names} />
                      {list.hasNextPage
                        ? (
                            <div className="mt-3 flex justify-center">
                              <Button size="sm" color="secondary" onClick={() => list.fetchNextPage()} isDisabled={list.isFetchingNextPage} data-testid="work-load-more">
                                {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
                              </Button>
                            </div>
                          )
                        : null}
                    </>
                  )}
        </div>
      </Body>
    </>
  )
}
